import React, { useState } from 'react';
import {
  Box,
  Checkbox,
  FormControlLabel,
  IconButton,
  InputAdornment,
  Link,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { useFlow } from '../../contexts/FlowContext';
import { FlowDivider } from '../components/FlowDivider';
import { FlowInputError } from '../components/FlowInputError';
import { FlowLoadingOverlay } from '../components/FlowLoadingOverlay';
import { FlowPrimaryButton } from '../components/FlowPrimaryButton';
import { FlowTextField } from '../components/FlowTextField';
import { SocialLoginButtons } from '../components/SocialLoginButtons';
import {
  validateEmail,
  validateFullName,
  validatePassword,
} from '../authValidation';
import { FlowAuthMethod, useFlowAuth } from '../hooks/useFlowAuth';
import { ChatbooksAuthResult, MIN_PASSWORD_LENGTH } from '../../utils/chatbooksAuth';
import { StudioFlowColors, StudioFlowRadii } from '../studioFlowTheme';

export const SignUpScreen: React.FC = () => {
  const formId = 'studio-web-sign-up-form';
  const navigate = useNavigate();
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));
  const { state, updateState } = useFlow();
  const [showPassword, setShowPassword] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [touched, setTouched] = useState({
    name: false,
    email: false,
    password: false,
  });

  const persistAuth = async (
    authResult: ChatbooksAuthResult,
    authMethod: FlowAuthMethod,
  ) => {
    updateState({
      authMethod,
      email: authResult.email,
      accessToken: authResult.accessToken,
      personId: authResult.personId,
      displayName: authResult.displayName || state.name.trim(),
      chatbooksEnvironment: authResult.environment,
      password: '',
      name: '',
      sessionId: '',
      sessionKey: '',
    });
    navigate('/phone');
  };

  const {
    handleSignUp,
    handleSSO,
    isBusy,
    busyMessage,
    externalError,
  } = useFlowAuth({ onAuthenticated: persistAuth });

  const nameValidationError = touched.name ? validateFullName(state.name) : null;
  const emailValidationError = touched.email ? validateEmail(state.email) : null;
  const passwordValidationError = touched.password ?
    validatePassword(state.password, MIN_PASSWORD_LENGTH) :
    null;

  const canSubmit =
    !validateFullName(state.name) &&
    !validateEmail(state.email) &&
    !validatePassword(state.password, MIN_PASSWORD_LENGTH) &&
    state.termsConsent &&
    !isBusy;

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setTouched({ name: true, email: true, password: true });
    setSubmitError('');

    if (!canSubmit) {
      return;
    }

    const result = await handleSignUp({
      fullName: state.name.trim(),
      email: state.email.trim(),
      password: state.password,
    });

    if (result?.error) {
      setSubmitError(result.error);
    }
  };

  const displayError = submitError || externalError;

  const signInLink = (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'baseline',
        gap: 1,
        my: 2,
      }}
    >
      <Typography
        sx={{
          color: StudioFlowColors.subtitle,
          fontSize: 13,
          letterSpacing: '-0.13px',
          fontFamily: StudioFlowColors.bodyFontFamily,
        }}
      >
        Have an account?
      </Typography>
      <Link
        component={RouterLink}
        to="/signin"
        underline="none"
        sx={{
          color: StudioFlowColors.title,
          fontWeight: 700,
          fontSize: 15,
          letterSpacing: '-0.15px',
          fontFamily: StudioFlowColors.bodyFontFamily,
        }}
      >
        Sign in
      </Link>
    </Box>
  );

  const formFields = (
    <Box sx={{ display: 'grid', gap: 2 }}>
      <Box>
        <FlowTextField
          placeholder="Name"
          autoComplete="name"
          value={state.name}
          onChange={(event) => updateState({ name: event.target.value })}
          onBlur={() => setTouched((current) => ({ ...current, name: true }))}
          inputProps={{ 'aria-label': 'Name' }}
          data-testid="signup-input-name"
        />
        {nameValidationError && <FlowInputError message={nameValidationError} />}
      </Box>
      <Box>
        <FlowTextField
          placeholder="Email address"
          type="email"
          autoComplete="email"
          value={state.email}
          onChange={(event) => updateState({ email: event.target.value })}
          onBlur={() => setTouched((current) => ({ ...current, email: true }))}
          inputProps={{ 'aria-label': 'Email address' }}
          data-testid="signup-input-email"
        />
        {emailValidationError && <FlowInputError message={emailValidationError} />}
      </Box>
      <Box>
        <FlowTextField
          placeholder="Password"
          type={showPassword ? 'text' : 'password'}
          autoComplete="new-password"
          value={state.password}
          onChange={(event) => updateState({ password: event.target.value })}
          onBlur={() => setTouched((current) => ({ ...current, password: true }))}
          inputProps={{
            'aria-label': 'Password',
            minLength: MIN_PASSWORD_LENGTH,
          }}
          data-testid="signup-input-password"
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  aria-label="Toggle password visibility"
                  onClick={() => setShowPassword((current) => !current)}
                  edge="end"
                  sx={{ color: StudioFlowColors.title }}
                >
                  {showPassword ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
        {passwordValidationError && <FlowInputError message={passwordValidationError} />}
        {!passwordValidationError &&
          state.password.length > 0 &&
          state.password.length < MIN_PASSWORD_LENGTH && (
            <Typography
              sx={{
                color: StudioFlowColors.subtitle,
                fontSize: 13,
                fontFamily: StudioFlowColors.bodyFontFamily,
                mt: 0.5,
              }}
            >
              Password must be at least {MIN_PASSWORD_LENGTH} characters
            </Typography>
        )}
      </Box>
      <FormControlLabel
        control={
          <Checkbox
            checked={state.termsConsent}
            onChange={(event) => updateState({ termsConsent: event.target.checked })}
            sx={{
              color: StudioFlowColors.primary,
              '&.Mui-checked': { color: StudioFlowColors.primary },
            }}
          />
        }
        label={
          <Typography
            variant="caption"
            sx={{ color: StudioFlowColors.subtitle, fontFamily: StudioFlowColors.bodyFontFamily }}
          >
            I agree to the Chatbooks Terms of Service and Privacy Policy
          </Typography>
        }
      />
    </Box>
  );

  const signUpButton = (
    <FlowPrimaryButton type="submit" form={formId} disabled={!canSubmit}>
      {isBusy ? 'Creating account…' : 'Sign up'}
    </FlowPrimaryButton>
  );

  const socialSection = (
    <>
      <FlowDivider label="Or, continue with" />
      <SocialLoginButtons
        onGoogle={() => handleSSO('google')}
        disabled={isBusy}
      />
    </>
  );

  return (
    <Box
      sx={{
        minHeight: '100vh',
        bgcolor: StudioFlowColors.background,
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Box
        sx={{
          flex: 1,
          display: 'flex',
          justifyContent: 'center',
          alignItems: isDesktop ? 'center' : 'flex-start',
          px: { xs: 3, md: 4 },
          pt: { xs: 2, md: 6 },
          pb: { xs: 14, md: 6 },
        }}
      >
        <Box
          component="form"
          id={formId}
          onSubmit={handleSubmit}
          sx={{
            width: '100%',
            maxWidth: isDesktop ? 566 : 402,
            ...(isDesktop && {
              bgcolor: StudioFlowColors.background,
              borderRadius: `${StudioFlowRadii.card}px`,
              p: 6,
            }),
          }}
        >
          <Typography
            sx={{
              fontFamily: StudioFlowColors.displayFontFamily,
              color: StudioFlowColors.title,
              fontWeight: 400,
              fontSize: { xs: 34, md: 44 },
              lineHeight: 1,
              textAlign: 'center',
              mb: { xs: 3, md: 4 },
            }}
          >
            Sign up for a Chatbooks account
          </Typography>

          <Box sx={{ maxWidth: 354, mx: isDesktop ? 'auto' : 0, width: '100%' }}>
            {formFields}
            {signInLink}
            {displayError && <FlowInputError message={displayError} />}

            {isDesktop ? (
              <>
                <Box sx={{ mt: displayError ? 2 : 0 }}>{signUpButton}</Box>
                <Box sx={{ mt: 3 }}>{socialSection}</Box>
              </>
            ) : (
              <>
                {socialSection}
              </>
            )}
          </Box>
        </Box>
      </Box>
      {!isDesktop && (
        <Box
          sx={{
            position: 'fixed',
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 10,
            bgcolor: StudioFlowColors.background,
            px: 3,
            pt: 2,
            pb: 'calc(16px + env(safe-area-inset-bottom))',
          }}
        >
          {signUpButton}
        </Box>
      )}
      {isBusy && (
        <FlowLoadingOverlay
          message={busyMessage || 'Creating your account'}
          fullScreen
        />
      )}
    </Box>
  );
};
